import { useState } from 'react';
import type { Deal, TabState } from '../../types';
import { buildSCList, PRODUCT_BUCKETS, bucketProducts, matchesCQFilter } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { median, avgArr } from '../../utils/format';
import { useDeals } from '../../context/DealsContext';
import { MetricCard } from '../ui/MetricCard';
import { SectionHeader } from '../ui/SectionHeader';
interface Props {
  state: TabState;
}
function SCMatrix({
  region,
  sub,
  cqState,
  data
}: {
  region: string;
  sub: string;
  cqState: TabState;
  data: Deal[];
}) {
  const ipData = data.filter(d => d.status === 'In Progress' && (region === 'Global' || d.region === region) && (!sub || sub === 'All' || d.sub_region === sub) && matchesCQFilter(d, cqState));
  const COLS = [...PRODUCT_BUCKETS.map(b => b.label), 'Other'];
  const scMap: Record<string, Record<string, number>> = {};
  const seenDR: Record<string, number> = {};
  for (const d of ipData) {
    if (!scMap[d.sc]) scMap[d.sc] = {};
    const bkts = bucketProducts(d.products || '');
    for (const bkt of bkts) {
      const uniq = d.sc + '|' + bkt + '|' + d.id;
      if (seenDR[uniq]) continue;
      seenDR[uniq] = 1;
      scMap[d.sc][bkt] = (scMap[d.sc][bkt] || 0) + 1;
    }
  }
  const scRows = Object.entries(scMap).map(([name, bkts]) => ({
    name: name.split(' ')[0],
    full: name,
    bkts,
    total: Object.values(bkts).reduce((a, b) => a + b, 0)
  })).sort((a, b) => b.total - a.total);
  const colTotals: Record<string, number> = {};
  for (const c of COLS) colTotals[c] = 0;
  for (const row of scRows) {
    for (const c of COLS) colTotals[c] += row.bkts[c] || 0;
  }
  const grandTotal = Object.values(colTotals).reduce((a, b) => a + b, 0);
  const activeCols = COLS.filter(c => colTotals[c] > 0);
  if (scRows.length === 0 || grandTotal === 0) {
    return <tr data-id="element-182">
        <td colSpan={2} style={{
        padding: '12px 10px',
        color: '#8892a0',
        fontSize: 10,
        fontStyle: 'italic'
      }} data-id="element-183">
          No in-progress deals match the current filter.
        </td>
      </tr>;
  }
  return <>
      <thead data-id="element-184">
        <tr data-id="element-185">
          <th data-id="element-186">SC</th>
          {activeCols.map(c => <th key={c} data-id="element-187">{c}</th>)}
          <th data-id="element-188">Total</th>
        </tr>
      </thead>
      <tbody data-id="element-189">
        {scRows.map(row => <tr key={row.full} data-id="element-190">
            <td title={row.full} data-id="element-191">{row.name}</td>
            {activeCols.map(c => <td key={c} data-id="element-192">
                {(row.bkts[c] || 0) > 0 ? <span className="mx-val" data-id="element-193">{row.bkts[c]}</span> : <span style={{
            color: '#d1d5db'
          }} data-id="element-194">—</span>}
              </td>)}
            <td data-id="element-195"><span className="mx-val" style={{
            background: '#e0e7ff',
            color: '#3730a3'
          }} data-id="element-196">{row.total}</span></td>
          </tr>)}
      </tbody>
      <tfoot data-id="element-197">
        <tr data-id="element-198">
          <td data-id="element-199">Total</td>
          {activeCols.map(c => <td key={c} data-id="element-200"><span style={{
            fontWeight: 700,
            color: '#1d4ed8'
          }} data-id="element-201">{colTotals[c] || 0}</span></td>)}
          <td data-id="element-202"><span style={{
            fontWeight: 700,
            color: '#3730a3'
          }} data-id="element-203">{grandTotal}</span></td>
        </tr>
      </tfoot>
    </>;
}
export function ODPanel({
  state
}: Props) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const {
    deals
  } = useDeals();
  const scData = buildSCList(state.r, state.sub, state, deals);
  const allIP = deals.filter(d => {
    if (d.status !== 'In Progress') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });

  // oppGnarr includes ALL statuses (not just IP), matching region/sub/CQ
  const allForGnarr = deals.filter(d => {
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const oppGnarr = allForGnarr.reduce((s, d) => s + (d.opp_gnarr || 0), 0);
  const ages = allIP.map(d => d.age);
  const maxIP = Math.max(...scData.map(s => s.ip), 1);
  return <div data-id="element-204">
      {/* Metrics */}
      <div className="mrow" data-id="element-205">
        <MetricCard label="In-progress deals" value={allIP.length} sub={`${scData.length} active SCs`} colorClass="c-blue" data-id="element-206" />
        <MetricCard label="Requests / person" value={scData.length ? +(allIP.length / scData.length).toFixed(1) : 0} sub="across active SCs" colorClass="c-purple" data-id="element-207" />
        <MetricCard label="Median age" value={`${median(ages)}d`} sub={`mean ${avgArr(ages)}d`} colorClass="c-gold" data-id="element-208" />
        <MetricCard label="GNARR Influenced" value={fmtG(oppGnarr)} sub="total opp GNARR (created qtr)" colorClass="c-green" data-id="element-209" />
      </div>

      {/* SC workload table */}
      <SectionHeader title="SC workload — in-progress deals (click row to expand)" data-id="element-210" />
      <div className="tw" data-id="element-211">
        <table className="tbl" data-id="element-212">
          <thead data-id="element-213">
            <tr data-id="element-214">
              <th style={{
              width: '28%'
            }} data-id="element-215">SC Name</th>
              <th style={{
              width: '22%'
            }} data-id="element-216">In-progress</th>
              <th style={{
              width: '16%'
            }} data-id="element-217">Avg age</th>
              <th style={{
              width: '16%'
            }} data-id="element-218">Median age</th>
              <th style={{
              width: '18%'
            }} data-id="element-219">Book GNARR</th>
            </tr>
          </thead>
          <tbody data-id="element-220">
            {scData.map((p, idx) => <>
                <tr key={p.n} onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)} data-id="element-221">
                  <td data-id="element-222">
                    <span className="name-link" data-id="element-223">
                      {p.n}
                      <span className={`chev${expandedIdx === idx ? ' open' : ''}`} data-id="element-224">▾</span>
                    </span>
                  </td>
                  <td data-id="element-225">
                    <div className="pbg" style={{
                  width: 80
                }} data-id="element-226">
                      <div className="pf" style={{
                    width: `${Math.round(p.ip / maxIP * 100)}%`
                  }} data-id="element-227" />
                    </div>
                    <strong data-id="element-228">{p.ip}</strong>
                  </td>
                  <td style={{
                color: p.avgAge > 90 ? '#f87171' : p.avgAge > 40 ? '#d4a017' : '#344054',
                fontWeight: p.avgAge > 40 ? 600 : 400
              }} data-id="element-229">
                    {p.avgAge}d
                  </td>
                  <td data-id="element-230">{p.med}d</td>
                  <td data-id="element-231">{fmtG(p.gnarr)}</td>
                </tr>
                {expandedIdx === idx && <tr key={`exp-${idx}`} data-id="element-232">
                    <td colSpan={5} style={{
                padding: 0
              }} data-id="element-233">
                      <div className="exp-inner" data-id="element-234">
                        <div style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#667085',
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '.05em'
                  }} data-id="element-235">
                          In-progress book — {p.n} ({p.ip} deals · {fmtG(p.gnarr)} GNARR)
                        </div>
                        <div className="deal-grid" data-id="element-236">
                          {p.deals.map(d => <div key={d.id} className="deal-chip" data-id="element-237">
                              <div className="dc-id" data-id="element-238">{d.id}</div>
                              <div className="dc-name" data-id="element-239">{d.account.substring(0, 35)}</div>
                              <div className="dc-tags" data-id="element-240">
                                <span className={`badge ${d.age > 90 ? 'b-red' : d.age > 45 ? 'b-amber' : 'b-gray'}`} data-id="element-241">{d.age}d</span>
                                <span className="badge b-blue" data-id="element-242">{fmtG(d.gnarr)}</span>
                                <span className="badge b-purple" style={{
                          fontSize: 8
                        }} data-id="element-243">{d.close_q}</span>
                                {d.age > 90 && <span className="badge b-red" data-id="element-244">⚠ aged</span>}
                              </div>
                              <div style={{
                        fontSize: 9,
                        color: '#8892a0',
                        marginTop: 3
                      }} data-id="element-245">
                                {d.sub_region} · {d.offering.substring(0, 26)}
                              </div>
                            </div>)}
                        </div>
                      </div>
                    </td>
                  </tr>}
              </>)}
          </tbody>
        </table>
      </div>

      {/* Solution × SC matrix */}
      <SectionHeader title="Solution × SC matrix — product concentration" data-id="element-246" />
      <div className="tw" data-id="element-247">
        <div className="sub" data-id="element-248">
          In-progress deals by product bucket per SC. Reveals single-threading and coverage gaps.
        </div>
        <div style={{
        overflowX: 'auto'
      }} data-id="element-249">
          <table className="tbl" id="od-matrix-tbl" style={{
          minWidth: 500
        }} data-id="element-250">
            <SCMatrix region={state.r} sub={state.sub} cqState={state} data={deals} data-id="element-251" />
          </table>
        </div>
      </div>
    </div>;
}